import React, { useState } from 'react'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import Label from 'components/ui/label'
import Tag from 'components/ui/tag'
import TextField from 'components/ui/text-field'
import styles from './index.module.css'

/**
 * A coupon code input component with validation, application, and removal functionality
 * @param caption - The button text for applying the coupon - defaults to 'Apply discount'
 * @param onSubmit - Callback function called when coupon is submitted, should return a promise with billing manifest
 * @param onRemove - Callback function called when applied coupon is removed
 * @param submitDisabled - Whether the submit button is disabled - defaults to false
 * @returns A div element containing the coupon code input form with validation and feedback
 */
const CouponCode = ({
  caption = 'Apply discount',
  onSubmit,
  onRemove,
  submitDisabled,
}: {
  caption?: React.ReactNode
  onSubmit: (...args: Array<any>) => any
  onRemove: (...args: Array<any>) => any
  submitDisabled?: boolean
}): React.ReactElement<'div'> => {
  const [code, setCode] = useState('')
  const [appliedCode, setAppliedCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notApply, setNotApply] = useState(false)
  const [message, setMessage] = useState(null)

  const submit = async () => {
    setLoading(true)
    setNotApply(false)
    setError(null)

    try {
      const preview = await onSubmit(code)
      const manifest = preview.current_billing_manifest
      const couponComponent = manifest?.line_items?.find(
        ({ kind }) => kind === 'coupon',
      )
      setMessage(couponComponent?.memo)
      setAppliedCode(code)
      setLoading(false)

      if (couponComponent && manifest.total_discount_in_cents === 0) {
        setNotApply(true)
        return setAppliedCode('')
      }

      if (preview.error?.error_code === 'coupon_not_applicable') {
        setNotApply(true)
        return
      }

      if (preview.error) {
        setError(preview.error.error_msg)
        setAppliedCode('')
      }

      if (!couponComponent && !manifest.total_discount_in_cents) {
        setError('This field is not valid. Please update it.')
        setAppliedCode('')
      }
    } catch (err) {
      console.error('CouponCode submit error', err) // TODO: don't silence errors
    }

    setLoading(false)
  }

  const remove = () => {
    setCode('')
    setAppliedCode('')
    setMessage(null)
    setError(null)
    setNotApply(false)
    onRemove()
  }

  return (
    <div className={styles.couponCode}>
      <Label caption="Coupon code" />
      <ButtonContainer>
        <TextField
          placeholder="Insert coupon code"
          error={error}
          value={code}
          onChange={({ target }) => setCode(target.value)}
        />
        <Button
          disabled={!code || code === appliedCode || submitDisabled}
          caption={caption}
          appearance="primary"
          onClick={submit}
          progressIndicator={loading}
          progressCaption="Loading"
        />
      </ButtonContainer>
      {notApply && (
        <div className={styles.couponCodeCouponCodeAlert}>
          <Alert status="warning" icon="warning">
            The coupon code doesn’t apply to the plan you’ve selected. Please
            check the code and{' '}
            <ContactUsLink intercomMsg="I have a question about a coupon">
              contact us
            </ContactUsLink>{' '}
            with any questions.
          </Alert>
        </div>
      )}
      {appliedCode && (
        <div className={styles.couponCodeTagContainer}>
          <Tag tag={appliedCode} onRemoveClick={remove} />{' '}
          <span className={styles.couponCodeCodeMessage}>{message}</span>
        </div>
      )}
    </div>
  )
}

export default CouponCode
